// Libs
import clsx from "clsx";
import { type FormikErrors, FormikProvider, useField, useFormik } from "formik";
import { useState } from "react";
import { v4 as uuid } from "uuid";

// Utils
import { orderBy } from "lodash";
import { capitalizeFirst } from "src/utils/formatters";
import { formatSlotToTime } from "src/utils/hourSlots";
import { computeMinMaxEdgesForDefaultValueSelect } from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioUtils";
import {
    MARKETING_ACTIONS_CREATE_VIEW_TK,
    MARKETING_ACTIONS_TRANSLATION_NAMESPACE,
} from "src/views/marketing-actions/config";
import {
    YupSchemaScenarioBuilder,
    generateUpdatedEdgesScenariosList,
} from "src/views/marketing-actions/marketing-actions-create-view/MarketingActionCreateForm.yupSchema";
import { buildBaseCurrentKey } from "src/views/marketing-actions/marketing-actions-create-view/MarketingActionCreateView.logic";

// Libs

// Hooks
import { getNextHalfHourTime } from "src/utils/timeUtils";
import { useConditionStyles } from "src/views/marketing-actions/components/form/ConditionCard/Condition.styles";
import { useScenarioCardStyles } from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioCard.styles";

// Types
import type { TFunction } from "src/design-system/core/TranslationContext";
import type { IdName } from "src/types/IdName";
import type { MarketingActionScenario } from "src/types/action/MarketingActionScenario";
import type { OfferTypeEnum } from "src/types/offer";
import type { ItemNameCategoryDiscount } from "src/types/offer/ItemNameCategoryDiscount";
import type { MarketingActionVariantEnum } from "src/utils/variants";
import { MultipleAutocompleteComponent } from "src/views/marketing-actions/components/form/MultipleAutocomplete";
import type { DisplayScenario } from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioDisplay";
import type { OfferDiscountType } from "src/views/marketing-actions/marketing-actions-create-view/MarketingAction.formator";

// Components
import { Box, Card, CardContent, Collapse, FormControlLabel, Switch } from "@mui/material";
import { Trans } from "react-i18next";
import { MarketingActionType } from "src/types/enum/MarketingActionType";
import type { CategoryItemList } from "src/types/offer/CategoryItemList";
import { CardTitle } from "src/views/marketing-actions/components/form/CardTitle";
import { CheeckyAlert } from "src/views/marketing-actions/components/form/CheeckyAlert";
import { AddScenarioDialog } from "src/views/marketing-actions/components/form/ScenarioCard/AddScenarioDialog";
import { DeleteScenarioDialog } from "src/views/marketing-actions/components/form/ScenarioCard/DeleteScenarioDialog";
import { ScenarioGrid } from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioGrid";

export const NO_SCENARIO_ID = "-1";

export interface CustomScenario {
    id?: number;
    updateScenarioId?: number;
    triggerValue?: number;
    typeDiscount?: OfferDiscountType;
    value?: OfferTypeEnum;
    discount?: string;
    cart?: number;
    products?: ItemNameCategoryDiscount[];
    categoryItemList?: Partial<CategoryItemList>[];
    audience: number;
    source?: string;
    triggerMin: number;
    triggerMax: number;
    isTemplate: boolean;
    uuid?: string;
    isHover?: boolean;
    isCheeckyTuesday?: boolean;
}

export interface SpecificScenarioField {
    name: string;
}

interface ScenarioCardProps {
    initialValues?: any;
    variants: MarketingActionVariantEnum[];
    values: any;
    validatedScenarios: CustomScenario[];
    t: TFunction;
    errors: FormikErrors<any>[];
    translateCommon: TFunction;
    audienceField: { name: string; option: IdName[] };
    isSpecialOperation: boolean;
    selectedChannel: string;
    selectedSource: string;
    selectedPosList: IdName[];
    setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean
    ) => Promise<void> | Promise<FormikErrors<any>>;
    setScenariosOnMainForm: (scenario: CustomScenario[]) => void;
    currentOptions: any;
    handleBlockPreorder: (value: any, channel: any) => void;
    currentTypeOptions: any;
    currentSpecialOperation: any;
    peakScenarioField: SpecificScenarioField;
    firstSlotScenarioField: SpecificScenarioField;
    startTime?: number;
    endTime?: number;
}

export function sortCustomScenarios<
    T extends Pick<CustomScenario | DisplayScenario, "id" | "triggerMin" | "triggerMax">[],
>(needToSortScenarios: T): T {
    needToSortScenarios.sort((a, b) => a.triggerMin - b.triggerMin);
    needToSortScenarios.forEach((scenario, index) => {
        scenario.id = index;
    });
    return needToSortScenarios;
}

export const sortScenarios = (
    needToSortScenarios: MarketingActionScenario[]
): MarketingActionScenario[] => {
    needToSortScenarios.sort(
        (a, b) => a.triggerStart.metaData.thresholdFactor - b.triggerStart.metaData.thresholdFactor
    );
    return needToSortScenarios.map((scenario, index) => ({
        ...scenario,
        id: index,
    }));
};

export function ScenarioCard({
    variants,
    values,
    initialValues,
    validatedScenarios,
    t,
    translateCommon,
    audienceField,
    isSpecialOperation,
    selectedChannel,
    selectedSource,
    selectedPosList,
    setFieldValue,
    errors,
    setScenariosOnMainForm,
    currentOptions,
    currentTypeOptions,
    handleBlockPreorder,
    currentSpecialOperation,
    firstSlotScenarioField,
    peakScenarioField,
    startTime,
    endTime,
}: ScenarioCardProps): JSX.Element {
    const classes = useScenarioCardStyles();
    const classesCondition = useConditionStyles();
    const [idDeleteScenario, setIdDeleteScenario] = useState(-1);
    const [idUpdateScenario, setIdUpdateScenario] = useState(null);
    const [serviceAndChannelField] = useField("serviceAndChannel");
    const [channelField] = useField("serviceAndChannel.channel.value");
    const [recurrenceField] = useField("recurrence");
    const [newScenarioId, setNewScenarioId] = useState<number | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [offerCharacteristicField] = useField("offerCharacteristic");
    const [firstSlotScenarioEnabledField] = useField(`${firstSlotScenarioField.name}Enabled`);
    const [firstSlotScenarioUuidField] = useField(`${firstSlotScenarioField.name}Uuid`);
    const [peakScenarioEnabledField] = useField(`${peakScenarioField.name}Enabled`);
    const [peakScenarioUuidField] = useField(`${peakScenarioField.name}Uuid`);

    // #region FORMIK

    const initialValuesScenarioTemp: CustomScenario = {
        audience: initialValues.audience.value,
        source: selectedSource,
        triggerMin: 0,
        triggerMax: 0,
        value: undefined,
        isTemplate: true,
    };

    const formikScenarioDialog = useFormik({
        initialValues: {
            scenarioTemp: initialValuesScenarioTemp,
        },
        enableReinitialize: true,
        validationSchema: YupSchemaScenarioBuilder({
            t,
            variants,
            source: selectedSource,
        }),
        onSubmit: (_, actions) => {
            const allScenario = values.scenarios ?? [];

            const isUpdate =
                valuesScenarioDialog?.scenarioTemp?.updateScenarioId !== undefined ? true : false;
            const oldScenario = isUpdate
                ? allScenario.find(
                      (sc) => sc.id === valuesScenarioDialog?.scenarioTemp?.updateScenarioId
                  )
                : null;
            const hasKeptEdges =
                isUpdate &&
                oldScenario.triggerMin === valuesScenarioDialog.scenarioTemp.triggerMin &&
                oldScenario.triggerMax === valuesScenarioDialog.scenarioTemp.triggerMax;

            const newScenario = {
                ...valuesScenarioDialog.scenarioTemp,
                id: isUpdate ? valuesScenarioDialog?.scenarioTemp?.updateScenarioId : newScenarioId,
                isTemplate: false,
                uuid: hasKeptEdges ? oldScenario.uuid : uuid(),
                isHover: false,
            };
            const { updateScenarioList } = generateUpdatedEdgesScenariosList(
                allScenario,
                newScenario,
                isUpdate
            );
            const sortedScenarios = sortCustomScenarios([...updateScenarioList, newScenario]);
            setScenariosOnMainForm(sortedScenarios);
            setIdUpdateScenario(null);

            if (
                !sortedScenarios.map((sc) => sc.uuid).includes(firstSlotScenarioUuidField.value) &&
                firstSlotScenarioUuidField.value !== NO_SCENARIO_ID
            ) {
                setFieldValue(firstSlotScenarioUuidField.name, null);
            }

            if (
                !sortedScenarios.map((sc) => sc.uuid).includes(peakScenarioUuidField.value) &&
                peakScenarioUuidField.value !== NO_SCENARIO_ID
            ) {
                setFieldValue(peakScenarioUuidField.name, null);
            }

            actions.resetForm();
        },
    });

    const {
        values: valuesScenarioDialog,
        setFieldValue: setFieldValuesScenarioDialog,
        handleSubmit: handleSubmitScenarioDialog,
        resetForm: resetFormScenarioDialog,
        validateForm: validateFormDialog,
    } = formikScenarioDialog;

    // #endregion

    // #region OPTIONS
    const baseCurrentKey = valuesScenarioDialog?.scenarioTemp.discount;
    const cartScenarioOptions = currentOptions?.cart[baseCurrentKey]?.values ?? [];

    const discountScenarioOptions =
        currentOptions?.discount[valuesScenarioDialog.scenarioTemp.value] ?? [];

    const maxItemScenario = currentOptions?.maxItems[valuesScenarioDialog.scenarioTemp.value];
    // #endregion

    // #region DIALOG FORM
    const openDialogForm = () => {
        setIsOpen(true);
    };

    const closeDialogForm = () => {
        setIsOpen(false);
    };

    const handleOpenDialogCreate = (scenarioId: number) => {
        setNewScenarioId(scenarioId);
        resetFormScenarioDialog();
        if (validatedScenarios !== undefined && validatedScenarios.length !== 0) {
            const defaultInputValues = computeMinMaxEdgesForDefaultValueSelect(
                scenarioId,
                validatedScenarios,
                false
            );
            setFieldValuesScenarioDialog("scenarioTemp.triggerMin", defaultInputValues.min);
            setFieldValuesScenarioDialog("scenarioTemp.triggerMax", defaultInputValues.max);
        }

        // Always reset value because we can come from an update scenario with a different audience value and we need options of the new audience
        setFieldValue("audience.value", initialValuesScenarioTemp.audience);

        openDialogForm();
    };

    const handleOpenDialogUpdate = async (scenarioId: number) => {
        const currentScenario = validatedScenarios.find((s) => s.id === scenarioId);
        const values = {
            ...currentScenario,
            audience: `${currentScenario.audience}`,
            updateScenarioId: scenarioId,
        };
        setFieldValuesScenarioDialog("scenarioTemp", values);

        setIdUpdateScenario(scenarioId);

        // Always reset value because we can come from an update scenario with a different audience value and we need options of the selected audience in the scenario
        setFieldValue("audience.value", currentScenario.audience);
        openDialogForm();
        if (
            errors &&
            errors[scenarioId] &&
            formikScenarioDialog.getFieldMeta("scenarioTemp.products").touched
        ) {
            await formikScenarioDialog.setFieldTouched("scenarioTemp.products", true);
            validateFormDialog();
        }
    };
    const handleOnSaveDialog = () => {
        if (formikScenarioDialog.isValid) {
            closeDialogForm();
            handleSubmitScenarioDialog();
        } else {
            handleSubmitScenarioDialog();
        }
    };

    const handleOnCancelDialog = () => {
        setNewScenarioId(null);
        setIdUpdateScenario(null);
        closeDialogForm();
    };
    // #endregion

    const scenarioOptions = [
        { id: NO_SCENARIO_ID, name: t("smart-specific-activation.no-scenario") },
    ].concat(
        sortCustomScenarios(validatedScenarios)
            .filter((scenario) => scenario.isTemplate === false)
            .map((scenario) => ({
                id: scenario.uuid,
                name: capitalizeFirst(`${translateCommon("concepts.scenario")} ${scenario.id + 1}`),
            }))
    );

    // Calcul pour avoir la demie-heure suivante pour l'affichage des horaires du premier créneau
    const startTimeNextHalfHour = getNextHalfHourTime(startTime);

    return (
        <>
            <Card>
                <CardContent>
                    <Box className={classes.marginBottomSmall}>
                        <CardTitle>{t("smart-card-title")}</CardTitle>
                    </Box>

                    <Box className={classes.ScenarioContainer}>
                        <ScenarioGrid
                            t={t}
                            errorsOnScenarios={errors}
                            channelField={channelField}
                            recurrenceField={recurrenceField}
                            setIdDeleteScenario={setIdDeleteScenario}
                            handleOpenDialogCreate={handleOpenDialogCreate}
                            handleOpenDialogUpdate={handleOpenDialogUpdate}
                            scenarios={sortCustomScenarios(
                                validatedScenarios.map((scenario) => ({
                                    id: scenario.id,
                                    offerType: scenario.value,
                                    typeDiscount: scenario.typeDiscount,
                                    cart: scenario.cart,
                                    isTemplate: scenario.isTemplate,
                                    discountValue: currentOptions?.discount[scenario.value]?.find(
                                        (discountOption) => discountOption.id === scenario.discount
                                    )?.value,
                                    audienceName: audienceField.option?.find(
                                        (a) => a.id === +scenario.audience
                                    )?.name,
                                    triggerMin: scenario.triggerMin,
                                    triggerMax: scenario.triggerMax,
                                    categoryItemList: scenario.categoryItemList,
                                    products: scenario.products.map((itemDiscount) => {
                                        return {
                                            itemName: itemDiscount.name,
                                            categoryName: itemDiscount?.categoryName,
                                            discount: {
                                                percentDiscount: itemDiscount.discountOption,
                                            },
                                        };
                                    }),
                                }))
                            )}
                        />
                    </Box>
                    {offerCharacteristicField?.value?.isCheeckyTuesday && (
                        <CheeckyAlert variantList={variants} />
                    )}

                    <Box
                        mb={2}
                        className={clsx(
                            classesCondition.labelBold,
                            classesCondition.marginBottomTitle
                        )}
                    >
                        {t("smart-specific-activation.title")}
                    </Box>
                    <Box mb={2} className={classes.subTitle}>
                        {t("smart-specific-activation.subtitle")}
                    </Box>
                    <div className={classes.specificCaseContainerContainer}>
                        <div
                            className={clsx(classes.specificCaseContainer, {
                                [classes.specificCaseContainerClosed]:
                                    !firstSlotScenarioEnabledField.value,
                                [classes.specificCaseContainerOpened]:
                                    firstSlotScenarioEnabledField.value,
                            })}
                        >
                            <div className={classes.specificCaseTitle}>
                                <FormControlLabel
                                    classes={{
                                        root: classes.specificCaseLabelContainer,
                                    }}
                                    value={firstSlotScenarioEnabledField.value}
                                    label={
                                        <span className={classes.specificCaseTitle}>
                                            <Trans
                                                i18nKey={`${MARKETING_ACTIONS_TRANSLATION_NAMESPACE}:${MARKETING_ACTIONS_CREATE_VIEW_TK}.smart-specific-activation.first-slot.title`}
                                                values={{
                                                    timeSlot:
                                                        startTime && endTime
                                                            ? `${formatSlotToTime(
                                                                  startTime,
                                                                  translateCommon
                                                              )} - ${formatSlotToTime(
                                                                  startTimeNextHalfHour,
                                                                  translateCommon
                                                              )}`
                                                            : "-",
                                                }}
                                                components={{ i: <i /> }}
                                            />
                                        </span>
                                    }
                                    labelPlacement="start"
                                    control={
                                        <Switch
                                            {...firstSlotScenarioEnabledField}
                                            onChange={(event) => {
                                                if (!event.target.checked) {
                                                    setFieldValue(
                                                        firstSlotScenarioUuidField.name,
                                                        null
                                                    );
                                                }
                                                firstSlotScenarioEnabledField.onChange(event);
                                            }}
                                            checked={firstSlotScenarioEnabledField.value}
                                        />
                                    }
                                />
                                <Collapse in={firstSlotScenarioEnabledField.value}>
                                    <MultipleAutocompleteComponent
                                        key={firstSlotScenarioUuidField.name}
                                        name={firstSlotScenarioUuidField.name}
                                        multiple={false}
                                        inputProps={{
                                            label: t("smart-specific-activation.first-slot.label"),
                                        }}
                                        options={scenarioOptions}
                                    />
                                </Collapse>
                            </div>
                        </div>
                        <div
                            className={clsx(classes.specificCaseContainer, {
                                [classes.specificCaseContainerClosed]:
                                    !firstSlotScenarioEnabledField.value,
                                [classes.specificCaseContainerOpened]:
                                    firstSlotScenarioEnabledField.value,
                            })}
                        >
                            <div className={classes.specificCaseTitle}>
                                <FormControlLabel
                                    classes={{
                                        root: classes.specificCaseLabelContainer,
                                    }}
                                    value={peakScenarioEnabledField.value}
                                    label={
                                        <span className={classes.specificCaseTitle}>
                                            <Trans
                                                i18nKey={`${MARKETING_ACTIONS_TRANSLATION_NAMESPACE}:${MARKETING_ACTIONS_CREATE_VIEW_TK}.smart-specific-activation.peak.title`}
                                                components={{ i: <i /> }}
                                            />
                                        </span>
                                    }
                                    labelPlacement="start"
                                    control={
                                        <Switch
                                            {...peakScenarioEnabledField}
                                            onChange={(event) => {
                                                if (!event.target.checked) {
                                                    setFieldValue(peakScenarioUuidField.name, null);
                                                }
                                                peakScenarioEnabledField.onChange(event);
                                            }}
                                            checked={peakScenarioEnabledField.value}
                                        />
                                    }
                                />
                                <Collapse in={peakScenarioEnabledField.value}>
                                    <MultipleAutocompleteComponent
                                        key={peakScenarioUuidField.name}
                                        name={peakScenarioUuidField.name}
                                        multiple={false}
                                        inputProps={{
                                            label: t("smart-specific-activation.peak.label"),
                                        }}
                                        options={scenarioOptions}
                                    />
                                </Collapse>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
            <FormikProvider value={formikScenarioDialog}>
                <AddScenarioDialog
                    t={t}
                    recurrenceValue={recurrenceField.value}
                    setFieldValueScenario={setFieldValuesScenarioDialog}
                    isUpdate={idUpdateScenario !== null}
                    idScenario={idUpdateScenario ?? newScenarioId}
                    validatedScenarios={validatedScenarios}
                    isOpen={isOpen}
                    audienceField={{
                        ...audienceField,
                        handleChange: (v) => {
                            setFieldValuesScenarioDialog("scenarioTemp", {
                                ...valuesScenarioDialog.scenarioTemp,
                                audience: v,
                                value: null,
                            });
                            setFieldValue("audience.value", v);
                        },
                    }}
                    onCancel={handleOnCancelDialog}
                    onSave={handleOnSaveDialog}
                    isSpecialOperation={isSpecialOperation}
                    maxItems={maxItemScenario}
                    serviceAndChannelField={serviceAndChannelField}
                    selectedChannel={selectedChannel}
                    selectedSource={selectedSource}
                    selectedPosList={selectedPosList}
                    setFieldValue={setFieldValue}
                    offerCharacteristicField={{
                        name: `scenarioTemp.value`,
                        autoCompleteProps: {
                            options: currentTypeOptions ? currentTypeOptions : [],
                            handleChange: (v) => {
                                const newScenarioTemp = {
                                    ...valuesScenarioDialog.scenarioTemp,
                                    triggerMin: valuesScenarioDialog.scenarioTemp.triggerMin,
                                    triggerMax: valuesScenarioDialog.scenarioTemp.triggerMax,
                                    products: [],
                                    categoryItemList: [],
                                    value: v.id,
                                    cart: null,
                                    discount: buildBaseCurrentKey({ type: v.id }),
                                    typeDiscount: null,
                                };
                                setFieldValuesScenarioDialog("scenarioTemp", newScenarioTemp);
                                handleBlockPreorder(v, values.serviceAndChannel.service.value);
                            },
                            inputProps: {
                                label: t(`steps.offer-type.select-offer-type`),
                            },
                        },
                    }}
                    categoryItemListField={{
                        name: "scenarioTemp.categoryItemList",
                        autoCompleteProps: {
                            options: [], // Option get in component
                            // biome-ignore lint/suspicious/noEmptyBlockStatements: legacy
                            handleChange: () => {}, // handleChange function is in OfferCharacteristicSharedInput because we need items list fetched inside him
                            inputProps: {
                                label: t(`${MarketingActionType.Offer}.categoryItemList.label`),
                            },
                        },
                    }}
                    productField={{
                        name: `scenarioTemp.products`,
                        autoCompleteProps: {
                            options: [], // Option get in component
                            // biome-ignore lint/suspicious/noEmptyBlockStatements: legacy
                            handleChange: () => {}, // handleChange function is in OfferCharacteristicSharedInput because we need items list fetched inside him
                            inputProps: {
                                label:
                                    maxItemScenario >= 100
                                        ? t("select-products")
                                        : t("select-products-with-max", { max: maxItemScenario }),
                            },
                        },
                    }}
                    discountField={{
                        name: `scenarioTemp.discount`,
                        autoCompleteProps: {
                            handleChange: (v) => {
                                const newOptions = currentOptions?.cart[v.id]?.values;

                                setFieldValuesScenarioDialog(
                                    `scenarioTemp.typeDiscount`,
                                    v.typeDiscount,
                                    true
                                );
                                if (newOptions && newOptions.length === 1) {
                                    setFieldValuesScenarioDialog(
                                        `scenarioTemp.cart`,
                                        newOptions[0]?.id,
                                        true
                                    );
                                } else if (
                                    !newOptions.some(
                                        (opt) => opt.id === valuesScenarioDialog.scenarioTemp.cart
                                    )
                                ) {
                                    setFieldValuesScenarioDialog("scenarioTemp.cart", null, true);
                                }
                            },
                            disabled:
                                isSpecialOperation &&
                                valuesScenarioDialog.scenarioTemp.discount === null &&
                                !currentSpecialOperation?.isMultipleDiscount,
                            options: orderBy(
                                discountScenarioOptions,
                                ["typeDiscount", "value"],
                                ["desc", "desc"]
                            ),
                            inputProps: {
                                label: t(`steps.offer-type.select-discount`),
                            },
                        },
                    }}
                    cartField={{
                        name: `scenarioTemp.cart`,
                        autoCompleteProps: {
                            options: orderBy(cartScenarioOptions, ["id"], ["desc"]),
                            disabled:
                                isSpecialOperation &&
                                valuesScenarioDialog.scenarioTemp.cart === null &&
                                !currentSpecialOperation?.isMultipleDiscount,
                            inputProps: {
                                label: t(`steps.offer-type.select-min-basket`),
                            },
                        },
                    }}
                />
            </FormikProvider>
            <DeleteScenarioDialog
                t={t}
                isOpen={idDeleteScenario !== -1}
                scenarioId={idDeleteScenario}
                onCancel={() => {
                    setIdDeleteScenario(-1);
                }}
                onSave={() => {
                    const deletedScenario = validatedScenarios.find(
                        (scenario) => scenario.id === idDeleteScenario
                    );
                    if (peakScenarioUuidField.value === deletedScenario.uuid) {
                        setFieldValue(peakScenarioUuidField.name, null);
                    }
                    if (firstSlotScenarioUuidField.value === deletedScenario.uuid) {
                        setFieldValue(firstSlotScenarioUuidField.name, null);
                    }

                    const newScenarios = validatedScenarios.filter(
                        (scenario) => scenario.id !== idDeleteScenario
                    );
                    const sortedScenarios = sortCustomScenarios(newScenarios);
                    setScenariosOnMainForm(sortedScenarios);
                    setIdDeleteScenario(-1);
                }}
            />
        </>
    );
}
